import './stylesheets/App.css'
import './stylesheets/Auth.css'
import {useCallback, useEffect, useMemo, useState}             from "react"
import {createTheme, CssBaseline, GlobalStyles, ThemeProvider} from '@mui/material'
import {createDark, createLight}                               from "./util/theme-colors"
import {lightTokens, darkTokens}                               from "./util/design-tokens"
import {AdapterDayjs}                                          from "@mui/x-date-pickers/AdapterDayjs"
import {LocalizationProvider}                                  from "@mui/x-date-pickers"
import AuthGate                                                from "./components/AuthGate"
import ThemeCssInjector                                        from "./components/ThemeCssInjector"
import {Helmet, HelmetProvider}                                from "react-helmet-async"
import PromptDebugProvider from "./components/debug/PromptDebugProvider"
import {useThemeStore}                                             from "./state"
import {resolveTheme, THEME_AUTO, THEME_DARK, THEME_LIGHT}         from "./util/theme-preference"

function App() {
	/*
	 * light / dark / auto (196). `preference` is what the student chose and is remembered across
	 * reloads; `themeName` is what actually renders, which for `auto` is whatever the system says.
	 */
	const {preference, setPreference} = useThemeStore()
	const [themeName, setThemeName] = useState(() => resolveTheme(preference))

	useEffect(() => {
		setThemeName(resolveTheme(preference))
	}, [preference])

	useEffect(() => {
		// only chase the system while the student has actually asked us to (the default)
		if (preference !== THEME_AUTO) return

		const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
		const handleThemeChange = (e) => setThemeName(e.matches ? THEME_DARK : THEME_LIGHT)

		mediaQuery.addEventListener('change', handleThemeChange)
		return () => mediaQuery.removeEventListener('change', handleThemeChange)
	}, [preference])

	const theme = useMemo(() => {
		const baseColors = themeName === 'dark' ? createDark() : createLight()
		return createTheme({
			typography: {
				fontFamily: 'Inter, Poppins, Arial, sans-serif',
			},
			palette: {
				mode: themeName,
				...baseColors,
			},
			// additive design-token layer from the v2 mockups (see util/design-tokens.js).
			// Read via useTheme().custom.* — does not affect the palette above.
			custom: themeName === 'dark' ? darkTokens : lightTokens,
		})
	}, [themeName])

	/*
	 * The nav menu's light/dark control. Signature and call sites unchanged -- but it now records an
	 * explicit preference, so the choice STICKS. It previously only moved `themeName`, which the
	 * system-change listener and the next reload both undid.
	 */
	const toggleTheme = useCallback(() => {
		setPreference(themeName === THEME_LIGHT ? THEME_DARK : THEME_LIGHT)
	}, [themeName, setPreference])

	return (
		<HelmetProvider>
			<Helmet>
				<title>Birdsy - Your AI-Powered Exam Study Partner</title>
				<meta
					name="description"
					content="Birdsy helps you prepare for career-defining exams with personalized practice and an adaptive AI tutor. Whether you're studying for your real estate license, a finance certification, or any professional exam, Birdsy tailors your learning experience to focus on your strengths and overcome your challenges. With engaging questions, instant feedback, and a study partner that adapts to your preferred style, Birdsy makes exam prep efficient, effective, and stress-free."
				/>
			</Helmet>
			<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en">
				<ThemeProvider theme={theme}>
					<GlobalStyles
						styles={{
							html: {
								backgroundColor: theme.palette.background.paper + ' !important',
							},
						}}
					/>
					<ThemeCssInjector/>
					<CssBaseline/>
					{/*
					  * Prompt debug mode (097) wraps the whole app: the toggle lives in the nav
					  * menu, the button lives on a chat message, and the drawer is mounted once
					  * here rather than per screen.
					  */}
					<PromptDebugProvider>
						<div className={`App ${themeName} env-${process.env.NODE_ENV}`}>
							<AuthGate toggleTheme={toggleTheme}/>
						</div>
					</PromptDebugProvider>
				</ThemeProvider>
			</LocalizationProvider>
		</HelmetProvider>
	)
}

export default App
